import axios from 'axios';
import React, { useState, useEffect, useRef } from 'react';
import {
   Button,
   Container,
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   TextField,
   IconButton,
   Grid,
   Select,
   MenuItem,
   FormControl,
   InputLabel,
   FormHelperText,
} from '@mui/material';
import { Delete, CloudUpload, Refresh, Close } from '@mui/icons-material';
import APIInstance from '../../Web/Instance';
import { getAuthToken } from '../../Redux/Service/FetchBaseQuery';
import { API_URL } from '../../Config';
import { toast } from 'react-toastify';
import { Link, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ClassicEditor from '../../Utils/RichEditor';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import InfiniteAutocomplete from '../../Components/InfiniteAutocomplete';
import { useGetTechnologyStackListQuery } from '../../Redux/Service/TechnologyStackApi';
import API from '../../Web/Instance';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { AddCircleOutline, RemoveCircleOutline } from '@mui/icons-material';
import StyledDashboardLayout from '../style';

export default function AddProposal() {
   const [pages, setPages] = useState([]);
   const [headerText, setHeaderText] = useState(''); // First page header text
   const [headerSubText, setHeaderSubText] = useState(''); // First page header subtext
   const [proposalName, setProposalName] = useState(''); // New state for proposal name
   const [openLayoutDialog, setOpenLayoutDialog] = useState(false);
   const [opportunities, setOpportunities] = useState([]); // State to store opportunities
   const [selectedOpportunity, setSelectedOpportunity] = useState(''); // State to store selected opportunity
   const [isGenerateButtonDisabled, setIsGenerateButtonDisabled] = useState(false);
   const [bannerImage, setBannerImage] = useState(null); // Added: Banner image state
   const [fileInputKey, setFileInputKey] = useState(Date.now()); // Added: Unique key for input reset
   const navigate = useNavigate();

   // State variables for infinite scroll
   const [search, setSearch] = useState('');
   const [page, setPage] = useState(1);
   const [visibleOptions, setVisibleOptions] = useState([]);
   const [selectedOption, setSelectedOption] = useState(null);
   const debounceTimeout = useRef(null);
   const [attemptedSubmit, setAttemptedSubmit] = useState(false); // track submit attempts to show validation

   // State variables for quotations and estimates
   const [quotations, setQuotations] = useState([]);
   const [estimates, setEstimates] = useState([]);
   const [selectedQuotation, setSelectedQuotation] = useState('');
   const [selectedEstimate, setSelectedEstimate] = useState('');
   const [selectedTechnologies, setSelectedTechnologies] = useState([]);
   const [techSections, setTechSections] = useState([{ id: Date.now(), technologies: [] }]);
   const [technologyOptions, setTechnologyOptions] = useState([]);

   // Define layouts once
   const layoutOptions = [
      { label: 'Left Image - Right Text', value: 'leftImage-rightText' },
      { label: 'Right Image - Left Text', value: 'rightImage-leftText' },
      { label: 'No Image - Only Text', value: 'noImage-OnlyText' },
      { label: 'USP (Heading & Title)', value: 'usp' },
      { label: 'Tech Stack (Title & Image)', value: 'techStack' },
      { label: 'Team (Name, Members, Timing)', value: 'team' },
      { label: 'Project Overview', value: 'project-overview' }, // Added from old version
   ];

   React.useEffect(() => {
      API.get(`technology-stack/list-technology`).then((response) => {
         setTechnologyOptions(response.data?.response);
      });
   }, []);

   useEffect(() => {
      // Fetch opportunities from API with pagination and search
      const fetchOpportunities = async () => {
         try {
            const token = getAuthToken();
            const response = await APIInstance.get(`${API_URL}opportunity/list`, {
               headers: {
                  Authorization: `Bearer ${token}`,
                  token,
               },
               params: {
                  limit: 10,
                  name: search,
                  page: page,
               },
            });

            const newData = response.data?.response?.data || [];

            // Add new opportunities to visible options (removing duplicates)
            setVisibleOptions((prevOptions) => {
               const existingIds = new Set(prevOptions.map((opt) => opt.id));
               const uniqueNewData = newData.filter((opt) => !existingIds.has(opt.id));
               return [...prevOptions, ...uniqueNewData];
            });

            setOpportunities(response.data?.response?.data);
         } catch (error) {
            console.log('Error fetching opportunities', error);
            toast.error('Error fetching opportunities. Please try again later.');
         }
      };
      fetchOpportunities();
   }, [search, page]);

   // Fetch quotations
   useEffect(() => {
      const fetchQuotations = async () => {
         try {
            const token = getAuthToken();
            const response = await APIInstance.get(`${API_URL}quotation`, {
               headers: {
                  Authorization: `Bearer ${token}`,
                  token,
               },
            });
            console.log('Fetching add proposal data...', response);
            // Ensure quotations is always an array
            const quotationsData = response.data?.response.data;
            setQuotations(Array.isArray(quotationsData) ? quotationsData : []);
         } catch (error) {
            console.error('Error fetching quotations:', error);
            toast.error('Failed to fetch quotations');
            setQuotations([]); // Set to empty array on error
         }
      };
      fetchQuotations();
   }, []);

   // Fetch estimates
   useEffect(() => {
      const fetchEstimates = async () => {
         try {
            const token = getAuthToken();
            const response = await APIInstance.get(`${API_URL}estimate`, {
               headers: {
                  Authorization: `Bearer ${token}`,
                  token,
               },
            });
            // Ensure estimates is always an array
            const estimatesData = response.data?.response;
            setEstimates(Array.isArray(estimatesData) ? estimatesData : []);
         } catch (error) {
            console.error('Error fetching estimates:', error);
            toast.error('Failed to fetch estimates');
            setEstimates([]); // Set to empty array on error
         }
      };
      fetchEstimates();
   }, []);

   const handleScroll = (event) => {
      const listboxNode = event.currentTarget;
      const bottom =
         listboxNode.scrollHeight - listboxNode.scrollTop <= listboxNode.clientHeight + 5;

      if (bottom) {
         setPage((prev) => prev + 1);
      }
   };

   const handleSelectOption = (event, value, reason) => {
      setSelectedOption(value);
      setSelectedOpportunity(reason === 'selectOption' ? value.id : '');
   };

   const handleSearch = (event, value) => {
      clearTimeout(debounceTimeout.current);
      debounceTimeout.current = setTimeout(() => {
         setSearch(value);
         setPage(1); // Reset page when search changes
         setVisibleOptions([]); // Clear previous results
      }, 300);
   };

   const handleAddPage = () => setOpenLayoutDialog(true);

   const handleSetLayout = (layout) => {
      const newPage = {
         id: Date.now(),
         layout,
         header: '',
         text: '',
         image: null,
      };

      // Initialize layout-specific structures
      if (layout === 'usp') {
         newPage.uspContent = [{ heading: '', title: '' }];
      }
      if (layout === 'techStack') {
         newPage.techSections = [{ name: 'Option 1', stacks: [] }];
      }
      if (layout === 'team') {
         newPage.teamSections = [
            {
               name: `Option ${pages.filter((p) => p.layout === 'team').length + 1}`,
               members: [{ position: '', members: '', allocation: '', file: null }],
            },
         ];
      }
      if (layout === 'project-overview') {
         newPage.text = ''; // init overview text
      }

      setPages([...pages, newPage]);
      setOpenLayoutDialog(false);
   };

   const handleUspChange = (pageId, index, field, value) => {
      setPages((prevPages) =>
         prevPages.map((p) =>
            p.id === pageId
               ? {
                    ...p,
                    uspContent: p.uspContent.map((u, i) =>
                       i === index ? { ...u, [field]: value } : u,
                    ),
                 }
               : p,
         ),
      );
   };

   const handleAddUspField = (pageId) => {
      setPages((prevPages) =>
         prevPages.map((p) =>
            p.id === pageId
               ? {
                    ...p,
                    uspContent: [...(p.uspContent || []), { heading: '', title: '' }],
                 }
               : p,
         ),
      );
   };

   const handleRemoveUspField = (pageId, indexToRemove) => {
      setPages((prevPages) =>
         prevPages.map((p) =>
            p.id === pageId
               ? {
                    ...p,
                    uspContent: p.uspContent.filter((_, i) => i !== indexToRemove),
                 }
               : p,
         ),
      );
   };

   const handleChange = (id, field, value) => {
      setPages(pages?.map((page) => (page.id === id ? { ...page, [field]: value } : page)));
   };

   // Updated Tech Stack handlers
   const handleAddTechnology = (pageId, sectionIndex, technologyId) => {
      const tech = technologyOptions.find((t) => t.id === technologyId)?.technology;
      if (!tech) return;
      setPages((prev) =>
         prev.map((p) => {
            if (p.id !== pageId) return p;
            const secs = p.techSections.map((s, i) =>
               i === sectionIndex && !s.stacks.includes(tech)
                  ? { ...s, stacks: [...s.stacks, tech] }
                  : s,
            );
            return { ...p, techSections: secs };
         }),
      );
   };

   const handleRemoveTechnology = (pageId, sectionIndex, techToRemove) => {
      setPages((prev) =>
         prev.map((p) => {
            if (p.id !== pageId) return p;
            const secs = p.techSections.map((s, i) =>
               i === sectionIndex
                  ? { ...s, stacks: s.stacks.filter((t) => t !== techToRemove) }
                  : s,
            );
            return { ...p, techSections: secs };
         }),
      );
   };

   const handleAddTechSection = (pageId) => {
      setPages((prev) =>
         prev.map((p) =>
            p.id === pageId
               ? {
                    ...p,
                    techSections: [
                       ...p.techSections,
                       {
                          name: `Option ${p.techSections.length + 1}`,
                          stacks: [],
                       },
                    ],
                 }
               : p,
         ),
      );
   };

   const handleRemoveTechSection = (pageId, sectionIndex) => {
      setPages((prevPages) =>
         prevPages.map((page) =>
            page.id === pageId && page.techSections.length > 1
               ? {
                    ...page,
                    techSections: page.techSections.filter((_, idx) => idx !== sectionIndex),
                 }
               : page,
         ),
      );
   };

   // Updated Team member handlers
   const handleTeamImageUpload = (pageId, sectionIndex, memberIndex, e) => {
      const file = e.target.files?.[0];
      if (file) {
         setPages((prev) =>
            prev.map((page) =>
               page.id === pageId
                  ? {
                       ...page,
                       teamSections: page.teamSections.map((sec, sIdx) =>
                          sIdx === sectionIndex
                             ? {
                                  ...sec,
                                  members: sec.members.map((m, mIdx) =>
                                     mIdx === memberIndex ? { ...m, file } : m,
                                  ),
                               }
                             : sec,
                       ),
                    }
                  : page,
            ),
         );
      }
   };

   const handleRemoveTeamImage = (pageId, sectionIndex, memberIndex) => {
      setPages((prev) =>
         prev.map((page) =>
            page.id === pageId
               ? {
                    ...page,
                    teamSections: page.teamSections.map((sec, sIdx) =>
                       sIdx === sectionIndex
                          ? {
                               ...sec,
                               members: sec.members.map((m, mIdx) =>
                                  mIdx === memberIndex ? { ...m, file: null } : m,
                               ),
                            }
                          : sec,
                    ),
                 }
               : page,
         ),
      );
      setFileInputKey(Date.now());
   };

   const handleAddTeamMember = (pageId, sectionIndex) => {
      setPages((prevPages) =>
         prevPages.map((p) =>
            p.id === pageId
               ? {
                    ...p,
                    teamSections: p.teamSections.map((s, idx) =>
                       idx === sectionIndex
                          ? {
                               ...s,
                               members: [
                                  ...s.members,
                                  {
                                     position: '',
                                     members: '',
                                     allocation: '',
                                     file: null,
                                  },
                               ],
                            }
                          : s,
                    ),
                 }
               : p,
         ),
      );
   };

   const handleRemoveTeamMember = (pageId, sectionIndex, memberIndex) => {
      setPages((prevPages) =>
         prevPages.map((p) =>
            p.id === pageId
               ? {
                    ...p,
                    teamSections: p.teamSections.map((s, idx) =>
                       idx === sectionIndex && s.members.length > 1
                          ? {
                               ...s,
                               members: s.members.filter((_, i) => i !== memberIndex),
                            }
                          : s,
                    ),
                 }
               : p,
         ),
      );
   };

   const handleTeamMemberChange = (pageId, sectionIndex, memberIndex, field, value) => {
      setPages((prevPages) =>
         prevPages.map((p) =>
            p.id === pageId
               ? {
                    ...p,
                    teamSections: p.teamSections.map((s, idx) =>
                       idx === sectionIndex
                          ? {
                               ...s,
                               members: s.members.map((m, i) =>
                                  i === memberIndex ? { ...m, [field]: value } : m,
                               ),
                            }
                          : s,
                    ),
                 }
               : p,
         ),
      );
   };

   const handleImageUpload = (id, e) => {
      const file = e.target.files[0];
      if (file) {
         setPages(pages?.map((page) => (page.id === id ? { ...page, image: file } : page)));
      }
   };

   const handleBannerImageChange = (e) => {
      const file = e.target.files[0];
      if (file) setBannerImage(file);
   };

   const handleResetImage = (id) => {
      const updatedPage = pages?.map((page) => (page.id === id ? { ...page, image: null } : page));
      setPages(updatedPage);
   };

   const handleDeletePage = (id) => {
      const updatedPages = pages.filter((page) => page.id !== id);
      setPages(updatedPages);
   };

   const handleQuotationChange = (e) => {
      setSelectedQuotation(e.target.value);
   };

   const handleEstimateChange = (e) => {
      setSelectedEstimate(e.target.value);
   };

   async function downloadPDF(url, fileName) {
      try {
         let newUrl = `${process.env.REACT_APP_API_URL}/downloads/${fileName}`;
         url = new URL(newUrl).href; // Ensure correct format
         const response = await axios.get(url, {
            responseType: 'blob', // Ensures we receive a binary file
         });
         const blobUrl = URL.createObjectURL(response.data);
         const a = document.createElement('a');
         a.href = blobUrl;
         a.download = `generated_proposal_${fileName}` || 'generated_proposal.pdf';
         document.body.appendChild(a); // Append to body
         a.click(); // Trigger the download
         document.body.removeChild(a); // Remove the element
         URL.revokeObjectURL(blobUrl); // Cleanup the URL object
         navigate('/proposal'); // Redirect to proposal list
      } catch (error) {
         toast.error('Error downloading the file. Please try again later.');
         console.error('Error downloading the file:', error);
      }
   }

   const handleSubmit = async () => {
      setAttemptedSubmit(true);
      if (!selectedOpportunity) {
         toast.error('Please select an opportunity before generating the proposal');
         return;
      }

      if (!proposalName.trim()) {
         toast.error('Please enter a proposal name');
         return;
      }

      const formData = new FormData();

      formData.append('title', proposalName);
      formData.append('headerText', headerText);
      formData.append('headerSubText', headerSubText);
      formData.append('selectedOpportunity', String(parseInt(selectedOpportunity)));

      if (selectedQuotation) {
         formData.append('selectedQuotation', String(parseInt(selectedQuotation)));
      }

      if (selectedEstimate) {
         formData.append('selectedEstimate', String(parseInt(selectedEstimate)));
      }

      if (bannerImage instanceof File) {
         formData.append('bannerImage', bannerImage);
      }

      // Process pages
      const validPages = pages
         .filter((page) => page != null)
         .map((page, index) => {
            const layout = page.layout || '';

            const basePageData = {
               layout,
               title: page.header || '',
               header: page.header || '',
               text: page.text || '',
               techStack: [],
               uspContent: [],
               teamMembers: [],
            };

            // Tech Stack Layout
            if (layout === 'techStack' && Array.isArray(page.techSections)) {
               basePageData.techStack = page.techSections
                  .map((section) => ({
                     name: section.name,
                     stacks: Array.isArray(section.stacks) ? section.stacks.filter(Boolean) : [],
                  }))
                  .filter((section) => section.stacks.length > 0);
            }

            // USP Layout
            if (layout === 'usp' && Array.isArray(page.uspContent)) {
               basePageData.uspContent = page.uspContent;
            }

            // Team Layout
            if (layout === 'team' && Array.isArray(page.teamSections)) {
               basePageData.teamMembers = page.teamSections.map((section) => ({
                  name: section.name,
                  members: section.members.map((member) => {
                     // Extract file from member to avoid circular reference
                     const { file, ...memberWithoutFile } = member;
                     return memberWithoutFile;
                  }),
               }));
            }

            return {
               ...basePageData,
               image: page.image instanceof File ? page.image : null,
            };
         });

      formData.append('pagesCount', String(validPages.length));

      // Append page data (excluding images)
      formData.append('pages', JSON.stringify(validPages.map(({ image, ...rest }) => rest)));

      // Append images
      validPages.forEach((page, index) => {
         if (page.image instanceof File) {
            formData.append(`pages[${index}][image]`, page.image);
         }

         // Handle team section images
         if (page.layout === 'team' && Array.isArray(page.teamMembers)) {
            page.teamMembers.forEach((section, sIndex) => {
               if (section.members && Array.isArray(section.members)) {
                  section.members.forEach((member, mIndex) => {
                     if (member.file instanceof File) {
                        formData.append(
                           `pages[${index}][teamMembers][${sIndex}][members][${mIndex}][image]`,
                           member.file,
                        );
                     }
                  });
               }
            });
         }
      });

      // Submit API
      try {
         setIsGenerateButtonDisabled(true);
         const token = getAuthToken();

         const response = await API.post('proposal', formData, {
            headers: {
               'Content-Type': 'multipart/form-data',
               Authorization: `Bearer ${token}`,
            },
         });

         const responseData = response.data;

         if (responseData?.statusCode === 201 && responseData?.response) {
            toast.success(responseData.message || 'Proposal created successfully');
         }

         if (responseData?.response?.pdfPath) {
            await downloadPDF(responseData.response.pdfPath, responseData.response.fileName);
         } else {
            navigate('/proposal');
         }
         // navigate('/proposal');
      } catch (error) {
         console.error(error);
         toast.error('Unable to generate PDF. Please try again later.');
      } finally {
         setIsGenerateButtonDisabled(false);
      }
   };

   return (
      <StyledDashboardLayout>
         <div className="content-wrapper">
            <div className="content-header">
               <div className="container-fluid">
                  <div className="d-flex justify-content-between mb-2">
                     <div className="pageTitle">
                        <h1 className="m-0">Add Proposal</h1>
                     </div>
                     <div className="btnWrap">
                        <Link to="/proposal" className="btn addBtn">
                           {' '}
                           <ArrowBackIcon /> Back
                        </Link>
                     </div>
                  </div>
               </div>
            </div>
            <div className="card customCard p-3 m-3">
               <Box className="card-body" sx={{ padding: '15px 0' }}>
                  <Grid container spacing={2}>
                     <Grid item xs={12}>
                        {/* <label htmlFor="proposalName">Proposal Name</label> */}
                        <TextField
                           required
                           fullWidth
                           label="Proposal Name"
                           value={proposalName}
                           onChange={(e) => setProposalName(e.target.value)}
                           error={attemptedSubmit && !proposalName.trim()}
                           helperText={
                              attemptedSubmit && !proposalName.trim()
                                 ? 'Proposal name is required'
                                 : ''
                           }
                           style={{ marginBottom: 20 }}
                        />
                     </Grid>
                     <Grid item xs={12}>
                        {/* <label htmlFor="opportunity">Opportunity</label> */}
                        <InfiniteAutocomplete
                           value={selectedOption}
                           onChange={(event, value, reason) =>
                              handleSelectOption(event, value, reason)
                           }
                           options={visibleOptions}
                           onScroll={handleScroll}
                           inputValue={search}
                           setInputValue={handleSearch}
                           total_options={'Opportunity'}
                           error={attemptedSubmit && !selectedOpportunity}
                           helperText={
                              attemptedSubmit && !selectedOpportunity
                                 ? 'Please select an opportunity'
                                 : ''
                           }
                        />
                     </Grid>

                     <Grid item xs={6}>
                        <FormControl fullWidth>
                           {/* <label htmlFor="quotation">Quotation (Optional)</label> */}
                           <InputLabel id="quotation-select-label">Quotation (Optional)</InputLabel>
                           <Select
                              labelId="quotation-select-label"
                              id="quotation-select"
                              value={selectedQuotation}
                              label="Quotation (Optional)"
                              onChange={handleQuotationChange}
                           >
                              <MenuItem value="">
                                 <em>None</em>
                              </MenuItem>
                              {Array.isArray(quotations) &&
                                 quotations.map((quotation) => (
                                    <MenuItem key={quotation.id} value={quotation.id}>
                                       {quotation.title}
                                    </MenuItem>
                                 ))}
                           </Select>
                        </FormControl>
                     </Grid>

                     <Grid item xs={6}>
                        <FormControl fullWidth>
                           {/* <label htmlFor="estimate">Estimate (Optional)</label> */}
                           <InputLabel id="estimate-select-label">Estimate (Optional)</InputLabel>
                           <Select
                              labelId="estimate-select-label"
                              id="estimate-select"
                              value={selectedEstimate}
                              label="Estimate (Optional)"
                              onChange={handleEstimateChange}
                           >
                              <MenuItem value="">
                                 <em>None</em>
                              </MenuItem>
                              {Array.isArray(estimates) &&
                                 estimates.map((estimate) => (
                                    <MenuItem key={estimate.id} value={estimate.id}>
                                       {estimate.name}
                                    </MenuItem>
                                 ))}
                           </Select>
                        </FormControl>
                     </Grid>

                     <Grid item xs={12}>
                        {/* <label htmlFor="text">Enter Header Text</label> */}
                        <TextField
                           required
                           fullWidth
                           label="Enter Header Text"
                           value={headerText}
                           onChange={(e) => setHeaderText(e.target.value)}
                           error={attemptedSubmit && !headerText.trim()}
                           helperText={
                              attemptedSubmit && !headerText.trim() ? 'Header text is required' : ''
                           }
                           style={{ marginBottom: 20 }}
                        />
                        {/* <label htmlFor="subText">Enter Header Sub text</label> */}
                        <TextField
                           required
                           fullWidth
                           label="Enter Header Sub text"
                           value={headerSubText}
                           onChange={(e) => setHeaderSubText(e.target.value)}
                           error={attemptedSubmit && !headerSubText.trim()}
                           helperText={
                              attemptedSubmit && !headerSubText.trim()
                                 ? 'Sub header text is required'
                                 : ''
                           }
                           style={{ marginBottom: 20 }}
                        />
                     </Grid>

                     {/* Banner Image Upload - Added from old version */}
                     <Grid item xs={12}>
                        {/* <label htmlFor="banner-file-input">Banner Image</label> */}
                        <Box display="flex" alignItems="center">
                           <input
                              accept="image/*"
                              type="file"
                              id="banner-file-input"
                              style={{ display: 'none' }}
                              onChange={handleBannerImageChange}
                           />
                           <label htmlFor="banner-file-input">
                              <Button
                                 variant="contained"
                                 component="span"
                                 startIcon={<CloudUpload />}
                                 className="commonBtn"
                              >
                                 Upload Banner
                              </Button>
                           </label>
                           {bannerImage && (
                              <IconButton
                                 color="secondary"
                                 onClick={() => setBannerImage(null)}
                                 sx={{ ml: 1 }}
                              >
                                 <Refresh />
                              </IconButton>
                           )}
                        </Box>
                        {bannerImage && (
                           <Box mt={2} mb={2}>
                              <img
                                 src={URL.createObjectURL(bannerImage)}
                                 alt="Banner preview"
                                 style={{
                                    width: '100%',
                                    maxHeight: 200,
                                    objectFit: 'cover',
                                    borderRadius: '4px',
                                 }}
                              />
                           </Box>
                        )}
                     </Grid>

                     {pages?.map((page) => (
                        <Grid item xs={12} key={page.id}>
                           <div
                              style={{
                                 display: 'flex',
                                 flexDirection:
                                    page.layout === 'leftImage-rightText'
                                       ? 'row'
                                       : page.layout === 'rightImage-leftText'
                                         ? 'row-reverse'
                                         : 'column',
                                 alignItems:
                                    page.layout === 'noImage-OnlyText' ? 'stretch' : 'center',
                                 padding: 10,
                                 border: '1px solid #ddd',
                                 borderRadius: 8,
                                 background: '#fff',
                                 marginBottom: 20,
                                 position: 'relative',
                              }}
                           >
                              {/* Upload Image (if layout supports it) */}
                              {['leftImage-rightText', 'rightImage-leftText'].includes(
                                 page.layout,
                              ) && (
                                 <div
                                    style={{
                                       display: 'flex',
                                       flexDirection: 'column',
                                       alignItems: 'center',
                                       marginRight: page.layout === 'leftImage-rightText' ? 10 : 0,
                                       marginBottom: page.layout === 'techStack' ? 10 : 0,
                                    }}
                                 >
                                    <input
                                       accept="image/*"
                                       type="file"
                                       style={{ display: 'none' }}
                                       id={`file-input-${page.id}`}
                                       onChange={(e) => handleImageUpload(page.id, e)}
                                    />
                                    <label htmlFor={`file-input-${page.id}`}>
                                       <IconButton color="primary" component="span">
                                          <CloudUpload fontSize="large" />
                                       </IconButton>
                                    </label>
                                    {page.image && (
                                       <IconButton
                                          color="secondary"
                                          onClick={() => handleResetImage(page.id)}
                                       >
                                          <Refresh />
                                       </IconButton>
                                    )}
                                 </div>
                              )}

                              {/* Content Area */}
                              <div style={{ flex: 1, width: '100%' }}>
                                 {/* Project Overview Layout - Added from old version */}
                                 {page.layout === 'project-overview' && (
                                    <>
                                       <Typography variant="h6" gutterBottom>
                                          Project Overview
                                       </Typography>
                                       <TextField
                                          label="Overview Content"
                                          fullWidth
                                          multiline
                                          rows={4}
                                          value={page.text || ''}
                                          onChange={(e) =>
                                             handleChange(page.id, 'text', e.target.value)
                                          }
                                          style={{ marginBottom: 10 }}
                                       />
                                    </>
                                 )}

                                 {/* Common Header Field */}
                                 {(page.layout === 'leftImage-rightText' ||
                                    page.layout === 'rightImage-leftText' ||
                                    page.layout === 'noImage-OnlyText') && (
                                    <>
                                       <TextField
                                          label="Header"
                                          fullWidth
                                          value={page.header || ''}
                                          onChange={(e) =>
                                             handleChange(page.id, 'header', e.target.value)
                                          }
                                          style={{ marginBottom: 10 }}
                                       />
                                       <div style={{ marginBottom: 10 }}>
                                          <label
                                             style={{
                                                marginBottom: 5,
                                                display: 'block',
                                             }}
                                          >
                                             Text
                                          </label>
                                          <CKEditor
                                             editor={ClassicEditor}
                                             data={page.text || ''}
                                             onChange={(_event, editor) =>
                                                handleChange(page.id, 'text', editor.getData())
                                             }
                                          />
                                       </div>
                                    </>
                                 )}

                                 {/* USP Layout */}
                                 {page.layout === 'usp' && (
                                    <>
                                       {(page.uspContent || []).map((usp, index) => (
                                          <Box
                                             key={index}
                                             display="flex"
                                             gap={2}
                                             alignItems="center"
                                             mb={2}
                                          >
                                             <TextField
                                                label={`Heading ${index + 1}`}
                                                fullWidth
                                                value={usp.heading}
                                                onChange={(e) =>
                                                   handleUspChange(
                                                      page.id,
                                                      index,
                                                      'heading',
                                                      e.target.value,
                                                   )
                                                }
                                             />
                                             <TextField
                                                label={`Title ${index + 1}`}
                                                fullWidth
                                                value={usp.title}
                                                onChange={(e) =>
                                                   handleUspChange(
                                                      page.id,
                                                      index,
                                                      'title',
                                                      e.target.value,
                                                   )
                                                }
                                             />
                                             {page.uspContent.length > 1 && (
                                                <IconButton
                                                   color="error"
                                                   onClick={() =>
                                                      handleRemoveUspField(page.id, index)
                                                   }
                                                >
                                                   <RemoveCircleOutline />
                                                </IconButton>
                                             )}
                                          </Box>
                                       ))}
                                       <Button
                                          variant="outlined"
                                          startIcon={<AddCircleOutline />}
                                          onClick={() => handleAddUspField(page.id)}
                                       >
                                          Add More
                                       </Button>
                                    </>
                                 )}

                                 {/* Tech Stack Layout - Updated with multiple sections */}
                                 {page.layout === 'techStack' && (
                                    <Box
                                       key={page.id}
                                       sx={{
                                          border: '1px solid grey',
                                          borderRadius: '8px',
                                          p: 2,
                                       }}
                                    >
                                       <Typography variant="h6" gutterBottom align="left">
                                          Technology Stack
                                       </Typography>

                                       {page.techSections?.map((section, idx) => (
                                          <Box
                                             key={idx}
                                             sx={{
                                                border: '1px dashed grey',
                                                borderRadius: '8px',
                                                padding: 2,
                                                mb: 2,
                                             }}
                                          >
                                             <Grid container spacing={2}>
                                                <Grid item xs={12}>
                                                   <Typography variant="subtitle1">
                                                      {section.name}
                                                   </Typography>
                                                </Grid>

                                                <Grid item xs={11}>
                                                   <Select
                                                      fullWidth
                                                      displayEmpty
                                                      onChange={(e) =>
                                                         handleAddTechnology(
                                                            page.id,
                                                            idx,
                                                            e.target.value,
                                                         )
                                                      }
                                                   >
                                                      <MenuItem value="" disabled>
                                                         Select Technology
                                                      </MenuItem>
                                                      {technologyOptions.map((option) => (
                                                         <MenuItem
                                                            key={option.id}
                                                            value={option.id}
                                                         >
                                                            {option.technology}
                                                         </MenuItem>
                                                      ))}
                                                   </Select>
                                                </Grid>

                                                <Grid item xs={1}>
                                                   {page.techSections.length > 1 && (
                                                      <Button
                                                         onClick={() =>
                                                            handleRemoveTechSection(page.id, idx)
                                                         }
                                                         variant="outlined"
                                                         color="error"
                                                         size="small"
                                                      >
                                                         Delete
                                                      </Button>
                                                   )}
                                                </Grid>

                                                <Grid item xs={12}>
                                                   <Typography variant="subtitle2">
                                                      Selected Technologies:
                                                   </Typography>
                                                   <Box>
                                                      {section.stacks?.map((tech, i) => (
                                                         <Box
                                                            key={i}
                                                            sx={{
                                                               display: 'flex',
                                                               alignItems: 'center',
                                                               mb: 1,
                                                            }}
                                                         >
                                                            <Typography variant="body2">
                                                               {tech}
                                                            </Typography>
                                                            <Button
                                                               onClick={() =>
                                                                  handleRemoveTechnology(
                                                                     page.id,
                                                                     idx,
                                                                     tech,
                                                                  )
                                                               }
                                                               startIcon={<DeleteIcon />}
                                                               size="small"
                                                               sx={{
                                                                  ml: 1,
                                                               }}
                                                            >
                                                               Remove
                                                            </Button>
                                                         </Box>
                                                      ))}
                                                   </Box>
                                                </Grid>
                                             </Grid>
                                          </Box>
                                       ))}

                                       <Button
                                          variant="contained"
                                          onClick={() => handleAddTechSection(page.id)}
                                          startIcon={<AddIcon />}
                                       >
                                          + Add Tech Stack Section
                                       </Button>
                                    </Box>
                                 )}

                                 {/* Team Layout - Updated with multiple sections */}
                                 {page.layout === 'team' && (
                                    <>
                                       <Typography variant="h6" gutterBottom>
                                          Team Structure
                                       </Typography>

                                       {page.teamSections?.map((section, sectionIndex) => (
                                          <Box key={sectionIndex} mb={4}>
                                             <Typography
                                                variant="subtitle1"
                                                gutterBottom
                                                fontWeight="bold"
                                             >
                                                {section.name}
                                             </Typography>

                                             {section.members.map((member, memberIndex) => (
                                                <Box
                                                   key={memberIndex}
                                                   sx={{
                                                      border: '1px solid #ccc',
                                                      borderRadius: 2,
                                                      padding: 2,
                                                      marginBottom: 2,
                                                      position: 'relative',
                                                   }}
                                                >
                                                   <Typography
                                                      variant="subtitle1"
                                                      gutterBottom
                                                      sx={{
                                                         fontWeight: 'bold',
                                                         mb: 1,
                                                      }}
                                                   >
                                                      Member {memberIndex + 1}
                                                   </Typography>
                                                   <Grid container spacing={2}>
                                                      <Grid item xs={12} sm={6}>
                                                         <TextField
                                                            label="Position"
                                                            fullWidth
                                                            value={member.position || ''}
                                                            onChange={(e) =>
                                                               handleTeamMemberChange(
                                                                  page.id,
                                                                  sectionIndex,
                                                                  memberIndex,
                                                                  'position',
                                                                  e.target.value,
                                                               )
                                                            }
                                                         />
                                                      </Grid>
                                                      <Grid item xs={12} sm={6}>
                                                         <TextField
                                                            label="Members"
                                                            type="number"
                                                            fullWidth
                                                            value={member.members || ''}
                                                            onChange={(e) =>
                                                               handleTeamMemberChange(
                                                                  page.id,
                                                                  sectionIndex,
                                                                  memberIndex,
                                                                  'members',
                                                                  e.target.value,
                                                               )
                                                            }
                                                         />
                                                      </Grid>
                                                      <Grid item xs={12} sm={6}>
                                                         <TextField
                                                            label="Allocation"
                                                            fullWidth
                                                            value={member.allocation || ''}
                                                            onChange={(e) =>
                                                               handleTeamMemberChange(
                                                                  page.id,
                                                                  sectionIndex,
                                                                  memberIndex,
                                                                  'allocation',
                                                                  e.target.value,
                                                               )
                                                            }
                                                         />
                                                      </Grid>
                                                      {/* <Grid item xs={12} sm={6}>
                                      <input
                                        key={
                                          fileInputKey +
                                          "-" +
                                          page.id +
                                          "-" +
                                          sectionIndex +
                                          "-" +
                                          memberIndex
                                        } // Unique key forces re-render
                                        accept="image/*"
                                        type="file"
                                        style={{ display: "none" }}
                                        id={`team-file-input-${page.id}-${sectionIndex}-${memberIndex}`}
                                        onChange={(e) =>
                                          handleTeamImageUpload(
                                            page.id,
                                            sectionIndex,
                                            memberIndex,
                                            e
                                          )
                                        }
                                      />
                                      <label
                                        htmlFor={`team-file-input-${page.id}-${sectionIndex}-${memberIndex}`}
                                      >
                                        <Button
                                          variant="outlined"
                                          component="span"
                                          startIcon={<CloudUpload />}
                                        >
                                          Upload Image
                                        </Button>
                                      </label>

                                      {member.file && (
                                        <Box mt={1}>
                                          <img
                                            src={URL.createObjectURL(member.file)}
                                            alt={`Team Member ${memberIndex + 1}`}
                                            style={{
                                              width: "100px",
                                              height: "auto",
                                              borderRadius: 4,
                                            }}
                                          />
                                          <Button
                                            onClick={() =>
                                              handleRemoveTeamImage(
                                                page.id,
                                                sectionIndex,
                                                memberIndex
                                              )
                                            }
                                            color="secondary"
                                            size="small"
                                            startIcon={<Refresh />}
                                          >
                                            Reset
                                          </Button>
                                        </Box>
                                      )}
                                    </Grid> */}

                                                      <Grid item xs={12}>
                                                         {section.members.length > 1 && (
                                                            <Button
                                                               variant="outlined"
                                                               color="error"
                                                               onClick={() =>
                                                                  handleRemoveTeamMember(
                                                                     page.id,
                                                                     sectionIndex,
                                                                     memberIndex,
                                                                  )
                                                               }
                                                            >
                                                               Remove Member
                                                            </Button>
                                                         )}
                                                      </Grid>
                                                   </Grid>
                                                </Box>
                                             ))}

                                             <Button
                                                variant="contained"
                                                color="primary"
                                                onClick={() =>
                                                   handleAddTeamMember(page.id, sectionIndex)
                                                }
                                                startIcon={<AddIcon />}
                                             >
                                                Add Member
                                             </Button>
                                          </Box>
                                       ))}
                                    </>
                                 )}
                              </div>

                              {/* Delete Page */}
                              <IconButton
                                 onClick={() => handleDeletePage(page.id)}
                                 style={{
                                    position: 'absolute',
                                    top: 5,
                                    right: 5,
                                 }}
                                 color="error"
                              >
                                 <Delete />
                              </IconButton>
                           </div>
                        </Grid>
                     ))}

                     <Grid
                        item
                        xs={12}
                        style={{
                           display: 'flex',
                           justifyContent: 'center',
                           gap: '10px',
                           marginTop: '20px',
                        }}
                     >
                        <Button onClick={handleAddPage} variant="contained" className="commonBtn">
                           Add Page
                        </Button>
                        <Button
                           onClick={handleSubmit}
                           disabled={isGenerateButtonDisabled}
                           variant="contained"
                           color="secondary"
                        >
                           Save & Generate PDF
                        </Button>
                        <Button
                           onClick={() => {
                              setPages([]); // Reset pages
                              setHeaderText(''); // First page header text
                              setHeaderSubText(''); // First page header subtext
                              setSelectedOpportunity(''); // State to store selected opportunity
                              setSelectedQuotation(''); // Reset quotation
                              setSelectedEstimate(''); // Reset estimate
                              setSelectedOption(null); // Reset selected option
                              setProposalName(''); // Reset proposal name
                              setBannerImage(null); // Reset banner image
                              setAttemptedSubmit(false); // Reset validation state
                           }}
                           variant="contained"
                           color="error"
                        >
                           Reset
                        </Button>
                     </Grid>
                  </Grid>
               </Box>
            </div>

            <Dialog
               open={openLayoutDialog}
               onClose={() => setOpenLayoutDialog(false)}
               sx={{
                  '& .MuiDialog-paper': {
                     maxWidth: '400px',
                     width: '100%',
                  },
               }}
            >
               <DialogTitle>Choose Layout Type</DialogTitle>
               <IconButton
                  aria-label="close"
                  onClick={() => setOpenLayoutDialog(false)}
                  color="primary"
                  startIcon={<Close />}
                  sx={(theme) => ({
                     position: 'absolute',
                     right: 8,
                     top: 8,
                     color: '#000',
                  })}
               >
                  <CloseIcon />
               </IconButton>
               <DialogContent>
                  <Select
                     fullWidth
                     onChange={(e) => handleSetLayout(e.target.value)}
                     displayEmpty
                     defaultValue=""
                  >
                     <MenuItem value="" disabled>
                        Select an option
                     </MenuItem>
                     {layoutOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                           {opt.label}
                        </MenuItem>
                     ))}
                  </Select>
               </DialogContent>
            </Dialog>
         </div>
      </StyledDashboardLayout>
   );
}
